// src/pages/VentESApp.js
import React from "react";
import { useLocation, Link } from "react-router-dom";
import Header from "../components/Header";
import apps from "../apps";

function VentESApp() {
  const location = useLocation();
  const app = apps.find(a => a.path === location.pathname);

  if (!app) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-6 py-8">
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Application Not Found
            </h1>
            <p className="text-gray-600 mb-6">
              The application you're looking for doesn't exist or has been removed.
            </p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-6 py-8">
        {/* App Header */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-start space-x-4 mb-6">
            <div className="text-5xl">{app.icon}</div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {app.name}
              </h1>
              <p className="text-lg text-gray-600 mb-4">{app.description}</p>
              <div className="flex items-center space-x-3">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {app.category}
                </span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  {app.vendor}
                </span>
                {app.integrationStatus === 'available' && (
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    ✓ Integration Active
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Integration Info */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            About {app.name}
          </h2>
          <p className="text-gray-600 mb-6">
            {app.name} is part of the VentES ecosystem, providing seamless integration
            with your dashboard. All data from this application is synchronized in
            real-time and accessible through the VentES AI assistant.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-2xl mb-2">🔄</div>
              <h3 className="font-semibold text-gray-900 mb-1">Real-time Sync</h3>
              <p className="text-sm text-gray-600">
                Data automatically synchronized with your dashboard
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-2xl mb-2">🤖</div>
              <h3 className="font-semibold text-gray-900 mb-1">AI Insights</h3>
              <p className="text-sm text-gray-600">
                Get intelligent insights from the VentES AI assistant
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-2xl mb-2">🔐</div>
              <h3 className="font-semibold text-gray-900 mb-1">Secure Access</h3>
              <p className="text-sm text-gray-600">
                Single sign-on through VentES authentication
              </p>
            </div>
          </div>
        </div>

        {/* Coming Soon: Direct App Access */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">🚀 Coming Soon</h2>
          <p className="text-blue-100 mb-6">
            Direct access to {app.name} will be available once the VentES main site
            integration is complete. You'll be able to launch and use the full application
            directly from this dashboard with seamless SSO authentication.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            >
              ← Back to Dashboard
            </Link>
            <button
              disabled
              className="inline-flex items-center px-6 py-3 bg-white bg-opacity-20 text-white rounded-lg font-medium cursor-not-allowed"
            >
              Launch App (Coming Soon)
            </button>
          </div>
        </div>

        {/* Placeholder for future app embedding or deep linking */}
        <div className="mt-6 bg-gray-100 rounded-xl p-8 border-2 border-dashed border-gray-300">
          <p className="text-gray-500 text-center">
            Future: Embedded {app.name} interface or direct deep link will appear here
          </p>
        </div>
      </div>
    </div>
  );
}

export default VentESApp;
